import { useEffect, useState } from 'react';
import { request } from '../api/client';
import { ConditionWord } from '../inventory/ConditionWord';
import { OemWord } from '../inventory/OemWord';
import { sharedDealStatusName } from '../sales/dealStatus';
import { useMounted } from '../ui/useMounted';

/**
 * Сделка глазами покупателя: то, что открывается по ссылке от продавца.
 *
 * <p>Без входа: учётной записи у покупателя нет и не будет. Права даёт секрет
 * в адресе, и отсюда же — что здесь показано. Только его собственная покупка,
 * без закупочной цены, без чужих сделок и без контактов: ссылку пересылают
 * в переписке, и попасть она может кому угодно.
 *
 * <p>Главный вопрос, ради которого клиент её открывает, — «до какого числа
 * отложено». Поэтому срок стоит первым, а не в конце списка.
 */
interface SharedItem {
  title: string | null;
  /**
   * Состояние кодом; у услуги пусто. Слово ставит `ConditionWord` — на языке
   * покупателя, а незнакомый код не печатается вовсе (задача 0318).
   */
  condition?: string | null;
  /**
   * Основной номер производителя, как хранится (задача 0329, ответ владельца
   * «Всё да»): покупатель опознаёт деталь по нему так же, как продавец
   * в выдаче. Основного нет — пусто; у услуги пусто.
   */
  primaryOem?: string | null;
  quantity: string;
  price: string;
}

interface SharedDeal {
  number: number | null;
  status: string;
  reservedUntil: string | null;
  total: string;
  paid: string;
  debt: string;
  items: SharedItem[];
}

export function SharedDealScreen({ company, token }: { company: string; token: string }) {
  const [deal, setDeal] = useState<SharedDeal | null>(null);
  const [missing, setMissing] = useState(false);
  // Почему это общий хук, а не ref с эффектом на месте, — в ui/useMounted.ts.
  const mounted = useMounted();

  useEffect(() => {
    void request<SharedDeal>(`/api/shared/${company}/${token}`)
      .then((found) => { if (mounted.current) setDeal(found); })
      // Просроченная и несуществующая ссылка неразличимы намеренно: клиенту
      // это одно и то же — «спросите у продавца», — а различие подсказывало бы
      // подбирающему, что он на верном пути.
      .catch(() => { if (mounted.current) setMissing(true); });
  }, [company, token, mounted]);

  if (missing) {
    return (
      <section className="screen">
        <h2>Ссылка не открывается</h2>
        <p className="note">
          Возможно, у неё истёк срок. Попросите продавца прислать новую.
        </p>
      </section>
    );
  }

  if (deal === null) {
    return <p className="note">Загружаем…</p>;
  }

  return (
    <section className="screen">
      <h2>Заказ {deal.number === null ? '' : `№ ${deal.number}`}</h2>

      <p className="note">
        {sharedDealStatusName(deal.status)}
        {deal.reservedUntil !== null && deal.status === 'RESERVED' && (
          <> до {new Date(deal.reservedUntil).toLocaleDateString('ru-RU')}</>
        )}
      </p>

      {/*
        Номера позиции здесь нет намеренно — та же причина, что у этикетки
        (`labels/labels.ts`): страница уезжает к покупателю по пересылаемой
        ссылке, а порядковый номер сказал бы ему, сколько позиций на складе.
        Записано в коде потому, что без этого следующий перебор поверхностей
        (задача 0168) читает пропуск как недоделку и «починит» его.
      */}
      <ul className="suggestions">
        {deal.items.map((item, at) => (
          <li key={at}>
            {item.title ?? 'позиция'}
            {/* «б/у» против «новая» — то, за что покупатель платит, а пометку
                в наименовании, которая говорила это до сих пор, снимают
                (0315, 0316). */}
            <ConditionWord code={item.condition} />
            <OemWord number={item.primaryOem} />
            {' '}— {Number(item.quantity)} шт. ×{' '}
            {Number(item.price).toLocaleString('ru-RU')} ₽
          </li>
        ))}
      </ul>

      <p className="note">
        Итого: {Number(deal.total).toLocaleString('ru-RU')} ₽
        {Number(deal.paid) > 0 && (
          <> · оплачено {Number(deal.paid).toLocaleString('ru-RU')} ₽</>
        )}
        {Number(deal.debt) > 0 && (
          <> · к оплате {Number(deal.debt).toLocaleString('ru-RU')} ₽</>
        )}
      </p>
    </section>
  );
}
